/**
 * LanguageSelector — globe-and-chevron dropdown replacement for Volto's
 * flat pipe-separated list. Used for both desktop header and mobile drawer.
 *
 * Originally DR-2026-054 (dev.ljusa-tider.se), redeployed for Vilax via
 * DR-2026-055. Refactored 2026-04-20 (BUG-2026-096) to use semantic CSS
 * classes instead of React inline styles, so VLT mobile-tools CSS overrides
 * can be neutralised cleanly per-context (no !important wars).
 *
 * Class contract:
 *   .language-selector
 *   .language-selector__toggle      (button)
 *   .language-selector__chevron     (svg, rotates when open)
 *   .language-selector__panel       (dropdown panel)
 *   .language-selector__option      (link)
 *   .language-selector__option--current
 *
 * Default appearance (light dropdown for desktop header) is declared in
 * src/vilax-layout.css. The Vilax-mobile-drawer override lives in
 * src/theme/_main.scss under the `.vilax-mobile-tools` scope.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';

const langNames = {
  en: 'English', sv: 'Svenska', de: 'Deutsch',
  bg: 'Български', ca: 'Català', cs: 'Čeština', da: 'Dansk',
  el: 'Ελληνικά', es: 'Español', et: 'Eesti', eu: 'Euskara',
  fi: 'Suomi', fr: 'Français', ga: 'Gaeilge', hr: 'Hrvatski',
  hu: 'Magyar', it: 'Italiano', ja: '日本語', ko: '한국어',
  lt: 'Lietuvių', lv: 'Latviešu', mt: 'Malti', nb: 'Norsk', nl: 'Nederlands',
  pl: 'Polski', pt: 'Português', 'pt-BR': 'Português (Brasil)',
  ro: 'Română', sk: 'Slovenčina', sl: 'Slovenščina',
  'zh-CN': '简体中文',
  hi: 'हिंदी', ta: 'தமிழ்', id: 'Indonesia', ms: 'Melayu',
  tl: 'Filipino', th: 'ไทย', sw: 'Kiswahili', zh: '中文',
};

const LanguageSelector = ({ onClickAction = () => {} }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );
  const isMultilingual = useSelector(
    (state) => state.site.data.features?.multilingual,
  );
  const availableLanguages = useSelector(
    (state) => state.site.data?.['plone.available_languages'],
  );

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!isMultilingual) {
    return <Helmet><html lang={toReactIntlLang(currentLang)} /></Helmet>;
  }

  const currentName = langNames[currentLang] || currentLang;

  return (
    <div className="language-selector" ref={ref}>
      <button
        type="button"
        className={`language-selector__toggle${open ? ' language-selector__toggle--open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Select language"
      >
        <svg className="language-selector__globe" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
        </svg>
        <span className="language-selector__label">{currentName}</span>
        <svg className="language-selector__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="language-selector__panel" role="menu">
          {availableLanguages?.map((lang) => {
            const translation = translations?.find((t) => t.language === lang);
            const name = langNames[lang] || lang;
            const isCurrent = toReactIntlLang(lang) === currentLang;
            return (
              <Link
                key={lang}
                to={translation ? flattenToAppURL(translation['@id']) : `/${lang}`}
                onClick={() => { setOpen(false); onClickAction(); }}
                className={`language-selector__option${isCurrent ? ' language-selector__option--current' : ''}`}
                aria-label={`Switch to ${name}`}
                role="menuitem"
              >
                {name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
